/**
 * Définitions des altérations d'état (buffs / debuffs / dots).
 * Chaque effet peut modifier des stats, infliger des dégâts sur la durée,
 * empêcher d'agir, etc.
 */

export const STATUS_EFFECTS = {
  // Dégâts sur la durée
  burn: {
    id: 'burn', name: 'Brûlure', icon: '🔥', type: 'debuff',
    dot: { pctMaxHp: 0.05, element: 'fire' },
    statMod: { degats: -0.10 },
    ignoresBarrier: true,
    desc: 'Inflige des dégâts de feu chaque tour et réduit l\'attaque. Ignore la barrière.'
  },
  poison: {
    id: 'poison', name: 'Poison', icon: '🧪', type: 'debuff',
    dot: { pctMaxHp: 0.04, element: 'nature' },
    ignoresBarrier: true,
    desc: 'Inflige des dégâts de nature chaque tour. Ignore la barrière.'
  },
  bleed: {
    id: 'bleed', name: 'Saignement', icon: '🩸', type: 'debuff',
    dot: { pctMaxHp: 0.06, element: 'physical' },
    ignoresBarrier: true,
    desc: 'Saignement physique progressif. Ignore la barrière.'
  },
  // Contrôle
  freeze: {
    id: 'freeze', name: 'Gel', icon: '❄️', type: 'debuff',
    statMod: { VIT: -0.30 },
    desc: 'Ralentit considérablement la cible.'
  },
  stun: {
    id: 'stun', name: 'Étourdissement', icon: '💫', type: 'debuff',
    skipTurn: true,
    desc: 'La cible saute son prochain tour.'
  },
  sleep: {
    id: 'sleep', name: 'Sommeil', icon: '💤', type: 'debuff',
    skipTurn: true,
    breakOnDamage: true,
    desc: 'La cible dort jusqu\'au prochain réveil ou dégât.'
  },
  paralysis: {
    id: 'paralysis', name: 'Paralysie', icon: '⚡', type: 'debuff',
    skipTurn: true,
    desc: 'La cible est tétanisée et saute son tour (ne se rompt pas au dégât).'
  },
  // Soins sur la durée
  regen: {
    id: 'regen', name: 'Régénération', icon: '💚', type: 'buff',
    hot: { pctMaxHp: 0.05 },
    desc: 'Restaure des PV à chaque tour.'
  },
  // Protection
  barrier: {
    id: 'barrier', name: 'Barrière', icon: '🛡️', type: 'buff',
    shield: { flat: 50 },
    desc: 'Absorbe une partie des dégâts subis.'
  },
  // Boosts de stats
  atkUp: {
    id: 'atkUp', name: 'Furie', icon: '⚔️', type: 'buff',
    statMod: { degats: 0.20 },
    desc: '+20% à TOUS les dégâts infligés.'
  },
  // Harmonisations élémentaires : +1 point d'affinité de l'élément
  // (= +50% de dégâts dans CET élément uniquement — voir combat.ts, le bonus
  // d'affinité multiplie les dégâts par (1 + affinité × 0.5)).
  fireAttune: {
    id: 'fireAttune', name: 'Cœur de Fournaise', icon: '🔥', type: 'buff',
    affinityMod: { fire: 1 },
    desc: '+50% aux dégâts de FEU uniquement (via +1 affinité Feu).'
  },
  iceAttune: {
    id: 'iceAttune', name: 'Harmonie du Givre', icon: '❄️', type: 'buff',
    affinityMod: { ice: 1 },
    desc: '+50% aux dégâts de GLACE uniquement (via +1 affinité Glace).'
  },
  lightningAttune: {
    id: 'lightningAttune', name: 'Harmonie de la Foudre', icon: '⚡', type: 'buff',
    affinityMod: { lightning: 1 },
    desc: '+50% aux dégâts de FOUDRE uniquement (via +1 affinité Foudre).'
  },
  waterAttune: {
    id: 'waterAttune', name: 'Harmonie des Eaux', icon: '💧', type: 'buff',
    affinityMod: { water: 1 },
    desc: '+50% aux dégâts d\'EAU uniquement (via +1 affinité Eau).'
  },
  earthAttune: {
    id: 'earthAttune', name: 'Harmonie de la Terre', icon: '🌍', type: 'buff',
    affinityMod: { earth: 1 },
    desc: '+50% aux dégâts de TERRE uniquement (via +1 affinité Terre).'
  },
  windAttune: {
    id: 'windAttune', name: 'Harmonie des Vents', icon: '💨', type: 'buff',
    affinityMod: { wind: 1 },
    desc: '+50% aux dégâts de VENT uniquement (via +1 affinité Vent).'
  },
  natureAttune: {
    id: 'natureAttune', name: 'Harmonie Sauvage', icon: '🌿', type: 'buff',
    affinityMod: { nature: 1 },
    desc: '+50% aux dégâts de NATURE uniquement (via +1 affinité Nature).'
  },
  holyAttune: {
    id: 'holyAttune', name: 'Harmonie Céleste', icon: '✨', type: 'buff',
    affinityMod: { holy: 1 },
    desc: '+50% aux dégâts SACRÉS uniquement (via +1 affinité Sacré).'
  },
  darkAttune: {
    id: 'darkAttune', name: 'Harmonie des Ombres', icon: '🌑', type: 'buff',
    affinityMod: { dark: 1 },
    desc: '+50% aux dégâts de TÉNÈBRES uniquement (via +1 affinité Ténèbres).'
  },
  allAttune: {
    id: 'allAttune', name: 'Grande Harmonie', icon: '🌈', type: 'buff',
    affinityMod: { all: 1 },
    desc: '+50% aux dégâts de TOUS les éléments (via +1 affinité globale).'
  },
  // Wards élémentaires : +3 résistance d'UN élément (1 pt ≈ −5% de dégâts
  // subis de cet élément — formule 100/(100+res×5)).
  fireWard: {
    id: 'fireWard', name: 'Écaille de Salamandre', icon: '🦎', type: 'buff',
    resistMod: { fire: 3 },
    desc: '+3 résistance de FEU (≈ −13% de dégâts de Feu subis).'
  },
  iceWard: {
    id: 'iceWard', name: 'Manteau de Givre', icon: '🧥', type: 'buff',
    resistMod: { ice: 3 },
    desc: '+3 résistance de GLACE (≈ −13% de dégâts de Glace subis).'
  },
  lightningWard: {
    id: 'lightningWard', name: 'Cage de Faraday', icon: '🗼', type: 'buff',
    resistMod: { lightning: 3 },
    desc: '+3 résistance de FOUDRE (≈ −13% de dégâts de Foudre subis).'
  },
  waterWard: {
    id: 'waterWard', name: 'Égide des Marées', icon: '🌊', type: 'buff',
    resistMod: { water: 3 },
    desc: '+3 résistance d\'EAU (≈ −13% de dégâts d\'Eau subis).'
  },
  earthWard: {
    id: 'earthWard', name: 'Assise Tellurique', icon: '⛰️', type: 'buff',
    resistMod: { earth: 3 },
    desc: '+3 résistance de TERRE (≈ −13% de dégâts de Terre subis).'
  },
  windWard: {
    id: 'windWard', name: 'Voile de Zéphyr', icon: '🪁', type: 'buff',
    resistMod: { wind: 3 },
    desc: '+3 résistance de VENT (≈ −13% de dégâts de Vent subis).'
  },
  natureWard: {
    id: 'natureWard', name: 'Écorce Vive', icon: '🪵', type: 'buff',
    resistMod: { nature: 3 },
    desc: '+3 résistance de NATURE (≈ −13% de dégâts de Nature subis).'
  },
  holyWard: {
    id: 'holyWard', name: 'Rempart de Lumière', icon: '🕯️', type: 'buff',
    resistMod: { holy: 3 },
    desc: '+3 résistance SACRÉE (≈ −13% de dégâts Sacrés subis).'
  },
  darkWard: {
    id: 'darkWard', name: 'Voile Contre-Ténèbres', icon: '🕯️', type: 'buff',
    resistMod: { dark: 3 },
    desc: '+3 résistance de TÉNÈBRES (≈ −13% de dégâts de Ténèbres subis).'
  },
  allWard: {
    id: 'allWard', name: 'Bastion Élémentaire', icon: '🏰', type: 'buff',
    resistMod: { all: 2 },
    desc: '+2 résistance de TOUS les éléments (≈ −9% de dégâts élémentaires subis).'
  },
  // 🔄 Contres : renvoient une partie des dégâts subis à l'attaquant.
  // Durée courte + cooldowns longs sur les sorts : un contre se PLACE au bon
  // moment (lire l'ennemi), il ne se spamme pas.
  physCounter: {
    id: 'physCounter', name: 'Posture de Riposte', icon: '🛡️', type: 'buff',
    counter: { pct: 0.5, kind: 'physical' },
    desc: 'Renvoie 50% des dégâts PHYSIQUES subis à l\'attaquant.'
  },
  magicCounter: {
    id: 'magicCounter', name: 'Miroir de Sorts', icon: '🪞', type: 'buff',
    counter: { pct: 0.5, kind: 'magical' },
    desc: 'Renvoie 50% des dégâts MAGIQUES subis au lanceur.'
  },
  fullCounter: {
    id: 'fullCounter', name: 'Loi du Talion', icon: '⚖️', type: 'buff',
    counter: { pct: 0.35, kind: 'all' },
    desc: 'Renvoie 35% de TOUS les dégâts subis à l\'attaquant.'
  },
  // Marques d'exposition : la cible ENCAISSE plus d'un élément → combos.
  fireBrand: {
    id: 'fireBrand', name: 'Marque de Cendre', icon: '🔥', type: 'debuff',
    resistMod: { fire: -2 },
    desc: '−2 résistance de FEU : la cible subit ≈ +11% de dégâts de Feu.'
  },
  darkBrand: {
    id: 'darkBrand', name: 'Marque d\'Ombre', icon: '🌑', type: 'debuff',
    resistMod: { dark: -2 },
    desc: '−2 résistance de TÉNÈBRES : la cible subit ≈ +11% de dégâts de Ténèbres.'
  },
  defUp: {
    id: 'defUp', name: 'Peau de Pierre', icon: '🛡️', type: 'buff',
    statMod: { armure: 0.25 },
    desc: 'Augmente l\'armure.'
  },
  vitUp: {
    id: 'vitUp', name: 'Hâte', icon: '💨', type: 'buff',
    statMod: { VIT: 0.25 },
    desc: 'Augmente la vitesse.'
  },
  intUp: {
    id: 'intUp', name: 'Concentration', icon: '🔮', type: 'buff',
    statMod: { INT: 0.20 },
    desc: 'Augmente l\'intelligence.'
  },
  // Affaiblissements
  defDown: {
    id: 'defDown', name: 'Brèche', icon: '🔓', type: 'debuff',
    statMod: { armure: -0.20 },
    desc: 'Réduit l\'armure.'
  },
  atkDown: {
    id: 'atkDown', name: 'Faiblesse', icon: '😰', type: 'debuff',
    statMod: { degats: -0.20 },
    desc: 'Réduit les dégâts infligés.'
  },
  // Résurrection
  reviveMark: {
    id: 'reviveMark', name: 'Faveur Divine', icon: '🕊️', type: 'buff',
    revive: { pctHp: 0.30 },
    desc: 'Ressuscite avec 30% de PV si KO.'
  },
  charmed: {
    id: 'charmed', name: 'Charmé', icon: '💖', type: 'debuff',
    charm: true,
    breakOnDamage: true,
    desc: 'La cible attaque ses anciens alliés ou soigne ses ennemis si elle ne peut pas combattre.'
  },
  allure: {
    id: 'allure', name: 'Allure Captivante', icon: '✨', type: 'buff',
    statMod: { CHA: 0.20 },
    desc: 'Augmente le charisme et la puissance des chants.'
  }
};

let _statusEffectsSource: any = null;

export function setStatusEffectsRegistry(registries: any) {
  _statusEffectsSource = registries?.statusEffects || null;
}

function getStatusEffects() {
  return _statusEffectsSource || STATUS_EFFECTS;
}

export function getStatusEffect(statusId: string) {
  return getStatusEffects()[statusId] || null;
}

export function isHarmful(statusId: string) {
  const s = getStatusEffects()[statusId];
  return s ? s.type === 'debuff' : false;
}
